"use client";

import { useTranslations } from "next-intl";
import { use } from "react";
import { useDateFormatter } from "react-aria";
import { SRCImage } from "react-datocms";
import { Balancer } from "react-wrap-balancer";

import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link } from "~/navigation";

import type { TourCardBlockProps } from "../_lib/gql";
import { Context } from "./context";

export function TourCard({ tour }: TourCardBlockProps) {
  const t = useTranslations("TourDetails");
  const { tourNodes } = use(Context);
  const inventory = tourNodes.find(
    ({ node }) => node.tour.id === tour.id,
  )?.inventory;

  const currencyFormatter = useCurrencyFormatter();
  const formatter = useDateFormatter();

  return (
    <div className="my-8">
      <Link
        target="_blank"
        className="shadow-outline group flex flex-col gap-5 rounded-3xl bg-neutral-200 p-5 md:grid md:grid-cols-[120px_1fr]"
        href={`/tours/${tour.slug}/${tour.id}`}
      >
        <div>
          <SRCImage
            imgClassName="rounded-xl"
            data={tour.heroImage.responsiveImage}
          />
        </div>
        <div>
          <div className="mb-1.5 line-clamp-2 font-medium leading-tight">
            <Balancer>{tour.title}</Balancer>
          </div>
          {tour.tripadvisorRating ? (
            <div className="mb-2">
              <TripadvisorRating size="small" value={tour.tripadvisorRating} />
            </div>
          ) : null}
          <div className="mt-2 sm:mt-3">
            <div className="text-xs text-neutral-500">
              {inventory?.date ? (
                <>
                  {t("available", { date: formatter.format(inventory.date) })}
                </>
              ) : (
                <>{t("bookNow")}</>
              )}
            </div>
            <div className="text-prune-500 text-sm font-medium">
              {inventory?.amount ? (
                <>
                  {t("fromAmount", {
                    currencyAmount: currencyFormatter(inventory.amount),
                  })}
                  {inventory.perPerson ? t("perPerson") : t("perGroup")}
                </>
              ) : (
                <>{t("bookNow")}</>
              )}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
